"use client";
import { Suspense, useEffect, useState } from "react";
import dynamic from "next/dynamic";
import { matchSorter } from "match-sorter";

import { observer, useStore } from "../../../service/mobx";

const ButtonFilled = dynamic(() => import("../../../component/Button/Filled"));
const TextField = dynamic(() => import("@mui/material/TextField"));
const CardHeader = dynamic(() => import("@mui/material/CardHeader"));
const Container = dynamic(() => import("@mui/material/Container"));
const Stack = dynamic(() => import("@mui/material/Stack"));
const Typography = dynamic(() => import("@mui/material/Typography"));
const Highlighter = dynamic(() => import("react-highlight-words"));
const ChatHistoryIcon = dynamic(() =>
  import("@mui-symbols-material/w400/ForumRounded")
);
const NavListItem = dynamic(() =>
  import("../../../component/Navigation").then(module => module.NavListItem)
);
const NewIcon = dynamic(() => import("@mui/icons-material/AddRounded"));

function Page() {
  const [matches, setMatches] = useState([]);
  const [searchTerms, setSearchTerms] = useState([]);
  const { agent, user, utilities } = useStore();

  useEffect(() => {
    if (searchTerms.length === 0) {
      return setMatches(agent.threadsArray);
    }

    const search = searchTerms.join(" ");
    const fuzzyMatches = matchSorter(agent.threadsArray, search, {
      keys: [
        { key: "history.*.text", threshold: matchSorter.rankings.MATCHES },
        {
          key: "history.*.sources.title",
          threshold: matchSorter.rankings.CONTAINS
        },
        { key: "history.*.sources.paperID" },
        {
          key: "history.*.sources.publisher",
          threshold: matchSorter.rankings.EQUAL
        },
        {
          key: "history.*.citations.title",
          threshold: matchSorter.rankings.CONTAINS
        },
        { key: "history.*.citations.paperID" },
        {
          key: "history.*.citations.publisher",
          threshold: matchSorter.rankings.EQUAL
        }
      ]
    });
    const matches = [];
    const fields = ["title", "paperID", "publisher"];

    for (const thread of fuzzyMatches) {
      const messages = new Set();

      for (const message of thread.history) {
        messages.add(message.text);

        const references = [
          ...(message.sources ?? []),
          ...(message.citations ?? [])
        ];

        for (const reference of references) {
          const text = [];

          for (const field of fields) {
            if (reference[field]) {
              text.push(reference[field]);
            }
          }

          messages.add(text.join(" "));
        }
      }

      const bestMatch = matchSorter([...messages], search);

      if (bestMatch.length) {
        matches.push({ ...thread, bestMatch });
      }
    }

    setMatches(matches);
  }, [agent.threadsArray, searchTerms]);

  return user.loaded === false ? null : (
    <Suspense>
      <Container maxWidth="expanded" sx={{ pb: 3 }}>
        <CardHeader
          avatar={
            <ChatHistoryIcon
              sx={{ height: 24, width: 24, color: "var(--secondary-color)" }}
            />
          }
          title="Your chat history"
          slotProps={{
            title: { variant: "titleLg", color: "var(--secondary-color)" }
          }}
          action={
            <ButtonFilled href="/agent" label="New chat" IconStart={NewIcon} />
          }
          sx={{ pb: 4 }}
        />
        <TextField
          fullWidth
          multiline
          minRows={4}
          disabled={agent.loaded === false}
          variant="outlined"
          placeholder={agent.loaded ? "Search your chats" : "Loading history"}
          onChange={event => {
            setSearchTerms(
              event.target.value.split(" ").filter(defined => defined)
            );
          }}
          sx={{
            mb: 3,
            borderRadius: "var(--shape-md-round)",
            boxShadow: "var(--shadows-3)",
            "& fieldset": {
              border: 1,
              transition: "all 1s",
              borderColor: "var(--outline-variant)"
            },
            "& .MuiInputBase-input": {
              color: "var(--surface-on-color)"
            },
            "& .MuiInputBase-input::placeholder": {
              opacity: 1,
              typography: "titleMd",
              color: "var(--secondary-color)"
            }
          }}
          slotProps={{
            input: {
              sx: {
                outline: "none",
                typography: "bodyLg",
                fontWeight: 600,
                borderRadius: "var(--shape-md-round)",
                bgcolor: "var(--surface-container-lowest)"
              }
            }
          }}
        />
        <Stack
          spacing={1}
          sx={{
            "& mark": {
              fontWeight: 700,
              bgcolor: "var(--primary-container)",
              color: "var(--primary-on-container)",
              borderRadius: "var(--shape-xs-round)"
            }
          }}
        >
          {matches.map(({ id, modified, history, bestMatch = [] }) => (
            <NavListItem
              key={id}
              href="/agent"
              line1={`Chat with ${history.length} message${
                history.length === 1 ? "" : "s"
              }${
                searchTerms.length === 0
                  ? ""
                  : ` - ${utilities.timeAgo(modified)}`
              }`}
              line1Sx={{ textTransform: "unset" }}
              sx={{
                maxHeight: "unset",
                height: "unset",
                bgcolor: "var(--surface-container-color)"
              }}
              line2={
                searchTerms.length === 0 ? (
                  `Last message ${utilities.timeAgo(modified)}`
                ) : (
                  <>
                    <Typography variant="bodyMd" component="p" mb={2}>
                      {`${bestMatch.length} fuzzy match${
                        bestMatch?.length === 1 ? "" : "es"
                      } found including:`}
                    </Typography>
                    <Highlighter
                      highlightTag="mark"
                      searchWords={searchTerms}
                      textToHighlight={bestMatch[0] || ""}
                    />
                  </>
                )
              }
              onClick={() => agent.set.threadId(id)}
            />
          ))}
        </Stack>
      </Container>
    </Suspense>
  );
}

export default observer(Page);
